<template>
  <el-dialog
    v-model="visible"
    title="迁移设备"
    width="680px"
    :close-on-click-modal="false"
    class="migrate-dialog"
    @close="handleClose"
  >
    <!-- 顶部说明区 -->
    <div class="dialog-hero">
      <div class="hero-icon-wrap">
        <el-icon :size="22" color="#fff"><Box /></el-icon>
      </div>
      <div class="hero-text">
        <h3>迁移设备协议 → 新终端</h3>
        <p>
          更换硬件后新板子本地没有设备协议文件，此处将缺失的协议文件重新下发。
          依据终端文件记录与船舶设备清单自动比对；运营协议出厂已预置，无需迁移。
        </p>
      </div>
    </div>

    <!-- 终端信息 -->
    <div class="endpoint-info">
      <span class="info-chip">终端 {{ deviceId || "—" }}</span>
      <span class="info-chip">已绑船舶 {{ shipName || "—" }}</span>
      <span
        class="info-chip"
        :class="rows.length > 0 ? 'chip-warn' : 'chip-ok'"
      >
        {{
          loading
            ? "比对中…"
            : rows.length > 0
            ? `待迁移 ${rows.length} 台`
            : "无需迁移"
        }}
      </span>
    </div>

    <!-- 无终端文件记录提示（通常为更换硬件后的新终端） -->
    <el-alert
      v-if="noRecord && !loading && rows.length > 0"
      type="info"
      :closable="false"
      class="no-record-alert"
      title="暂无终端文件记录（通常为更换硬件后的新终端），已列出全部设备待迁移；重复迁移无副作用（覆盖导入）"
    />

    <!-- 本轮迁移结果摘要 -->
    <el-alert
      v-if="lastRoundSummary"
      :type="lastRoundSummary.type"
      :closable="false"
      class="round-summary-alert"
      :title="lastRoundSummary.text"
    />

    <!-- 主体：比对中 / 待迁移表 / 无需迁移 -->
    <div v-loading="loading" class="dialog-body">
      <template v-if="rows.length > 0">
        <div class="table-head">
          <el-checkbox
            :model-value="allChecked"
            :indeterminate="checkedCount > 0 && checkedCount < checkableCount"
            @change="toggleCheckAll"
          >
            待迁移设备
          </el-checkbox>
          <span class="head-tip">
            默认全选；迁移后失败的设备保持勾选，可一键重试
          </span>
        </div>
        <div class="device-table">
          <div class="m-row m-row-head">
            <div class="m-cell cell-check"></div>
            <div class="m-cell cell-name">设备名称</div>
            <div class="m-cell cell-en">英文键名</div>
            <div class="m-cell cell-file">协议文件</div>
            <div class="m-cell cell-status">状态</div>
          </div>
          <div v-for="row in rows" :key="row.enName" class="m-row">
            <div class="m-cell cell-check">
              <el-checkbox
                v-model="row.checked"
                :disabled="!row.protName || row.success === true"
              />
            </div>
            <div class="m-cell cell-name">{{ row.devName || "—" }}</div>
            <div class="m-cell cell-en">{{ row.enName || "—" }}</div>
            <div class="m-cell cell-file">
              <span v-if="row.protName" class="file-text">{{
                row.protName
              }}</span>
              <span v-else class="file-missing">无协议文件记录</span>
            </div>
            <div class="m-cell cell-status">
              <span v-if="row.success === null" class="st-pending">待迁移</span>
              <span v-else-if="row.success" class="st-ok">✓ 已就位</span>
              <span v-else class="st-fail" :title="row.errorMsg || ''">
                ✕ {{ row.errorMsg || "失败" }}
              </span>
            </div>
          </div>
        </div>
      </template>
      <div v-else-if="!loading" class="all-done">
        <el-icon :size="40" color="#10b981"><CircleCheckFilled /></el-icon>
        <p>终端已持有全部设备协议文件，无需迁移</p>
      </div>
    </div>

    <!-- 已就位清单（有缺失时展示） -->
    <div
      v-if="!loading && readyFiles.length > 0 && rows.length > 0"
      class="ready-box"
    >
      <span class="ready-label">已就位 {{ readyFiles.length }} 台：</span>
      <el-tag
        v-for="f in readyFiles"
        :key="f.enName"
        size="small"
        type="success"
        effect="plain"
        class="ready-tag"
      >
        {{ displayName(f) }}
      </el-tag>
    </div>

    <template #footer>
      <el-button size="large" @click="visible = false">关闭</el-button>
      <el-button
        type="primary"
        size="large"
        class="migrate-btn"
        :loading="migrating"
        :disabled="checkedCount === 0 || loading"
        @click="migrate"
      >
        <el-icon><Promotion /></el-icon>
        <span>迁移选中设备（{{ checkedCount }}）</span>
      </el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { ElMessage } from "element-plus";
import { Box, Promotion, CircleCheckFilled } from "@element-plus/icons-vue";
import { migrateDeviceProtocolsApi } from "@/api/cert/certBatchApi";
import { listProtocolFilesApi } from "@/api/svc005Api";
import { loadTranslations, translateName } from "@/composables/useTranslation";

/** 待迁移行（对账缺失设备 + 勾选态 + 本轮迁移结果） */
interface MigrateRow {
  enName: string;
  devName: string;
  protName: string | null;
  /** null=待迁移（未参与本轮下发）；true/false=本轮 [5,6] 对账结果 */
  success: boolean | null;
  errorMsg: string | null;
  checked: boolean;
}

const props = defineProps<{
  modelValue: boolean;
  endpointId: string;
  deviceId?: string;
  shipId: string;
  shipName?: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const visible = ref(props.modelValue);
const loading = ref(false);
const migrating = ref(false);
const rows = ref<MigrateRow[]>([]);
/** 已就位文件（enName 为准；中文名展示走全局翻译缓存 translateName，不依赖缓存表 dev_name 列） */
const readyFiles = ref<{ enName: string }[]>([]);
/** 无终端文件记录（缓存为空：通常为换硬件后的新终端） */
const noRecord = ref(false);
const lastRoundSummary = ref<{ type: string; text: string } | null>(null);

const checkableCount = computed(
  () => rows.value.filter((r) => r.protName && r.success !== true).length
);
const checkedCount = computed(
  () =>
    rows.value.filter((r) => r.checked && r.protName && r.success !== true)
      .length
);
const allChecked = computed(
  () =>
    checkableCount.value > 0 &&
    rows.value.every((r) => !r.protName || r.success === true || r.checked)
);

watch(
  () => props.modelValue,
  (val) => {
    visible.value = val;
    if (val) {
      lastRoundSummary.value = null;
      loadList();
    }
  }
);
watch(visible, (val) => emit("update:modelValue", val));

/**
 * 读终端文件记录与船舶设备清单比对（零 MQTT 交互，秒开）。
 *
 * <p>数据源：svc_terminal_protocol_file 缓存表——用户端每次添加/修改/删除设备、
 * 迁移设备、[5,7] 同步后都会以终端返回列表全量刷新，平时即代表终端真实文件状态；
 * 换硬件时后端会清空该缓存（旧板子记录失真），此时无记录 = 新终端必然全缺。</p>
 */
const loadList = async () => {
  loading.value = true;
  rows.value = [];
  readyFiles.value = [];
  try {
    // 确保全局翻译缓存可用（App 启动已加载则幂等返回，展示设备中文名用）
    loadTranslations();
    const result = await listProtocolFilesApi(props.shipId);
    if (!result) {
      ElMessage.error("读取比对结果失败");
      return;
    }
    rows.value = (result.missingDevices || []).map((d) => ({
      enName: d.enName,
      devName: d.devName,
      protName: d.protName,
      success: null,
      errorMsg: null,
      checked: true, // 默认全勾（无可迁文件的行勾选被禁用）
    }));
    readyFiles.value = (result.files || [])
      .filter((f) => !!f.enName)
      .map((f) => ({ enName: f.enName as string }));
    // 无任何终端文件记录 = 换过硬件的新终端（正常添加过设备的船必有记录）
    noRecord.value = (result.files || []).length === 0;
  } catch (e: any) {
    ElMessage.error(e?.message || "读取比对结果失败");
  } finally {
    loading.value = false;
  }
};

/** 已就位设备显示名：全局翻译缓存（svc_device_name_translation）英文转中文，未命中显示英文 */
const displayName = (f: { enName: string }) => translateName(f.enName);

const toggleCheckAll = (val: boolean | string | number) => {
  rows.value.forEach((r) => {
    if (r.protName && r.success !== true) {
      r.checked = val === true;
    }
  });
};

/** 批量迁移勾选设备（[5,5] 下发，[5,6] 返回列表逐台对账，幂等可无限重试） */
const migrate = async () => {
  const targets = rows.value
    .filter((r) => r.checked && r.protName)
    .map((r) => r.enName);
  if (targets.length === 0) {
    ElMessage.warning("勾选的设备均无协议文件记录，无法迁移");
    return;
  }
  migrating.value = true;
  try {
    const result = await migrateDeviceProtocolsApi(props.endpointId, targets);
    lastRoundSummary.value = null;
    if (result.reason) {
      ElMessage.error(`迁移未完成：${result.reason}`);
      lastRoundSummary.value = {
        type: "error",
        text: `迁移未完成：${result.reason}，可重试`,
      };
    } else {
      const ok = result.results.filter((x) => x.success).length;
      const fail = result.total - ok;
      if (fail === 0) {
        ElMessage.success(`迁移成功：${ok} 台设备协议已就位`);
        lastRoundSummary.value = {
          type: "success",
          text: `迁移成功 ${ok} 台，全部就位`,
        };
      } else {
        ElMessage.warning(`本轮成功 ${ok} 台、失败 ${fail} 台，可继续重试`);
        lastRoundSummary.value = {
          type: "warning",
          text: `本轮成功 ${ok} 台、失败 ${fail} 台（失败设备已保持勾选，可直接重试）`,
        };
      }
    }
    // 重载比对结果：后端已用 [5,6] 列表刷缓存，成功者从待迁移消失、
    // 失败者保留——杜绝"已就位的行仍留在待迁移列表、可再次勾选下发"的问题
    await loadList();
    // 上轮失败结果合并回行（展示失败原因并保持勾选便于重试；成功行已不在列表）
    for (const r of rows.value) {
      const hit = result.results.find(
        (x) => x.enName === r.enName && !x.success
      );
      if (hit) {
        r.success = false;
        r.errorMsg = hit.errorMsg;
        r.checked = true;
      }
    }
  } catch (e: any) {
    ElMessage.error(e?.message || "迁移失败");
  } finally {
    migrating.value = false;
  }
};

const handleClose = () => {
  lastRoundSummary.value = null;
  noRecord.value = false;
};
</script>

<style scoped>
:deep(.migrate-dialog .el-dialog__header) {
  padding: 20px 24px 0;
}
:deep(.migrate-dialog .el-dialog__body) {
  padding: 16px 24px;
}
:deep(.migrate-dialog .el-dialog__footer) {
  padding: 12px 24px 20px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 顶部英雄区 */
.dialog-hero {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  border-radius: 12px;
  margin-bottom: 14px;
}
.hero-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #10b981, #059669);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}
.hero-text h3 {
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
}
.hero-text p {
  font-size: 12.5px;
  color: #475569;
  margin: 0;
  line-height: 1.6;
}

/* 终端信息 chips */
.endpoint-info {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.info-chip {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
}
.info-chip.chip-warn {
  background: #fef3c7;
  color: #b45309;
}
.info-chip.chip-ok {
  background: #ecfdf5;
  color: #047857;
}

.no-record-alert,
.round-summary-alert {
  margin-bottom: 12px;
  border-radius: 10px;
}

/* 主体 */
.dialog-body {
  min-height: 160px;
  border: 1px solid #e8ecf1;
  border-radius: 12px;
  padding: 12px;
}

.table-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.table-head :deep(.el-checkbox__label) {
  font-weight: 700;
  color: #334155;
}
.head-tip {
  font-size: 12px;
  color: #94a3b8;
}

/* 设备表 */
.device-table {
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
}
.m-row {
  display: flex;
  align-items: center;
  padding: 9px 12px;
  border-bottom: 1px solid #f8fafc;
  font-size: 13px;
}
.m-row:last-child {
  border-bottom: none;
}
.m-row-head {
  background: #f8fafc;
  font-weight: 700;
  color: #64748b;
  font-size: 12px;
}
.m-cell.cell-check {
  width: 32px;
  flex-shrink: 0;
}
.m-cell.cell-name {
  flex: 1.1;
  min-width: 0;
  color: #1e293b;
  font-weight: 600;
}
.m-cell.cell-en {
  flex: 1;
  min-width: 0;
  color: #475569;
  font-family: "SF Mono", Monaco, monospace;
  font-size: 12px;
}
.m-cell.cell-file {
  flex: 1.1;
  min-width: 0;
}
.m-cell.cell-status {
  flex: 0 0 110px;
  text-align: right;
}
.file-text {
  color: #64748b;
  font-family: "SF Mono", Monaco, monospace;
  font-size: 12px;
  word-break: break-all;
}
.file-missing {
  color: #ef4444;
  font-size: 12px;
}
.st-pending {
  color: #94a3b8;
  font-size: 12px;
}
.st-ok {
  color: #059669;
  font-weight: 700;
  font-size: 12px;
}
.st-fail {
  color: #dc2626;
  font-size: 12px;
  cursor: help;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 110px;
  vertical-align: middle;
}

/* 全部就位空态 */
.all-done {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 36px 0;
}
.all-done p {
  margin: 0;
  color: #059669;
  font-weight: 600;
  font-size: 14px;
}

/* 已就位清单 */
.ready-box {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.ready-label {
  font-size: 12.5px;
  font-weight: 700;
  color: #15803d;
}
.ready-tag {
  border-radius: 999px;
}

/* 迁移按钮 */
.migrate-btn {
  border-radius: 10px !important;
  padding: 0 24px !important;
  font-weight: 600 !important;
  background: linear-gradient(135deg, #10b981, #059669) !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
}
.migrate-btn:hover:not(:disabled) {
  box-shadow: 0 6px 18px rgba(16, 185, 129, 0.4) !important;
}
</style>
